const API_BASE =
  import.meta.env.DEV
    ? '/api/v1'
    : 'https://ultimate-bidder-api.frankpedersen84.workers.dev/api/v1';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message?: string) {
    super(message ?? code);
  }
}

function handleAuthFailure(path: string) {
  if (path === '/auth/me' || path === '/auth/login') return;
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/login') return;
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.replace(`/login?next=${next}`);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let code = 'unknown';
    let message: string | undefined;
    // Read the body ONCE as text, then try to parse it as JSON. (Calling
    // res.text() after res.json() throws "body already read", so we can't read
    // twice — read text first.)
    const raw = await res.text().catch(() => '');
    try {
      const data = JSON.parse(raw) as {
        error?: unknown; message?: string; success?: boolean;
      };
      // Plain { error: string } from app handlers.
      if (typeof data.error === 'string') {
        code = data.error;
      }
      // Zod-validator returns { success: false, error: { issues: [...] } }.
      // Surface the issue messages so the page can render them.
      else if (data.error && typeof data.error === 'object' && 'issues' in (data.error as object)) {
        const issues = (data.error as { issues?: Array<{ path?: string[]; message?: string }> }).issues ?? [];
        code = 'validation_error';
        message = issues.map((i) =>
          `${(i.path ?? []).join('.') || 'field'}: ${i.message ?? 'invalid'}`,
        ).join(' · ');
      }
      if (data.message && !message) message = data.message;
    } catch {
      // Rollout #23 — non-JSON error body (a 500 HTML page, a gateway error,
      // an empty body). Don't swallow it: surface the text / statusText so the
      // ApiError.message is a real diagnostic instead of an empty 'unknown'.
      code = `http_${res.status}`;
      message = raw.trim().slice(0, 200) || res.statusText || undefined;
    }
    if (res.status === 401) handleAuthFailure(path);
    throw new ApiError(res.status, code, message);
  }
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

// ─── Typed clients for user / team / project-member feature ───────────
import type {
  AdminUserListItem, InvitationListItem, InviteUserRequest, UpdateUserRequest,
  Team, TeamMember, CreateTeamRequest, UpdateTeamRequest, AddTeamMemberRequest, TeamMemberRole,
  ProjectMember, AddProjectMemberRequest, UpdateProjectMemberRequest, TransferProjectRequest,
  InviteInfo, AcceptInviteRequest,
} from '@ub/shared';

export const adminUsersApi = {
  list: () => api.get<{ items: AdminUserListItem[]; pendingInvitations: InvitationListItem[] }>('/admin/users'),
  invite: (body: InviteUserRequest) =>
    api.post<{
      id: string; email: string; name: string; expiresAt: string; inviteUrl: string;
      emailDelivered: boolean; emailError: string | null;
    }>('/admin/users/invite', body),
  resendInvite: (invitationId: string) =>
    api.post<{
      ok: boolean; expiresAt: string; inviteUrl: string;
      emailDelivered: boolean; emailError: string | null;
    }>(`/admin/invitations/${invitationId}/resend`),
  revokeInvite: (invitationId: string) =>
    api.delete<{ ok: boolean }>(`/admin/invitations/${invitationId}`),
  update: (userId: string, body: UpdateUserRequest) =>
    api.patch<{ ok: boolean }>(`/admin/users/${userId}`, body),
  suspend: (userId: string) => api.post<{ ok: boolean }>(`/admin/users/${userId}/suspend`),
  reactivate: (userId: string) => api.post<{ ok: boolean }>(`/admin/users/${userId}/reactivate`),
  resetPassword: (userId: string) => api.post<{ ok: boolean }>(`/admin/users/${userId}/reset-password`),
  delete: (userId: string) => api.delete<{ ok: boolean }>(`/admin/users/${userId}`),
};

export const teamsApi = {
  list: () => api.get<{ items: Team[] }>('/teams'),
  detail: (id: string) => api.get<{ team: Team; members: TeamMember[] }>(`/teams/${id}`),
  create: (body: CreateTeamRequest) => api.post<{ id: string; name: string }>('/teams', body),
  update: (id: string, body: UpdateTeamRequest) => api.patch<{ ok: boolean }>(`/teams/${id}`, body),
  delete: (id: string) => api.delete<{ ok: boolean }>(`/teams/${id}`),
  addMember: (id: string, body: AddTeamMemberRequest) =>
    api.post<{ ok: boolean }>(`/teams/${id}/members`, body),
  removeMember: (id: string, userId: string) =>
    api.delete<{ ok: boolean }>(`/teams/${id}/members/${userId}`),
  setMemberRole: (id: string, userId: string, teamRole: TeamMemberRole) =>
    api.post<{ ok: boolean }>(`/teams/${id}/members`, { userId, teamRole }),
};

export const projectMembersApi = {
  list: (projectId: string) =>
    api.get<{ items: ProjectMember[] }>(`/projects/${projectId}/members`),
  add: (projectId: string, body: AddProjectMemberRequest) =>
    api.post<{ ok: boolean }>(`/projects/${projectId}/members`, body),
  setRole: (projectId: string, userId: string, body: UpdateProjectMemberRequest) =>
    api.patch<{ ok: boolean }>(`/projects/${projectId}/members/${userId}`, body),
  remove: (projectId: string, userId: string) =>
    api.delete<{ ok: boolean }>(`/projects/${projectId}/members/${userId}`),
  transfer: (projectId: string, body: TransferProjectRequest) =>
    api.post<{ ok: boolean; previousOwnerId: string | null; newOwnerId: string }>(`/projects/${projectId}/transfer`, body),
  assignTeam: (projectId: string, teamId: string) =>
    api.post<{ ok: boolean; addedCount: number }>(`/projects/${projectId}/assign-team`, { teamId }),
  unassignTeam: (projectId: string) =>
    api.post<{ ok: boolean }>(`/projects/${projectId}/unassign-team`),
};

export const inviteApi = {
  info: (token: string) =>
    api.get<InviteInfo>(`/auth/invite-info?token=${encodeURIComponent(token)}`),
  accept: (body: AcceptInviteRequest) =>
    api.post<{ user: { id: string; email: string; name: string; role: string; companyId: string } }>(
      '/auth/accept-invite', body,
    ),
};

// ─── Offices + onboarding (admin) ─────────────────────────────────────
export interface OfficeItem {
  id: string; slug: string; name: string; address: string | null;
  city: string | null; state: string | null; postalCode: string | null;
  lat: number | null; lng: number | null; isHeadquarters: boolean;
}
export interface OfficeUpsertBody {
  name: string; address?: string; city?: string; state?: string;
  postalCode?: string; isHeadquarters?: boolean; lat?: number; lng?: number;
}
export interface NearbyAgency {
  name: string; type: string; city: string | null; state: string | null;
  lat: number | null; lng: number | null; distanceMi: number | null;
  alreadyTracked: boolean; matchedSourceId?: string;
}
export interface SetupOutcome {
  name: string;
  status: 'created' | 'staged' | 'blocked' | 'failed' | 'skipped_existing';
  sourceId?: string; enabled?: boolean; platform?: string;
  confidence?: number; reason?: string;
}
export interface SetupSummary {
  processed: number; created: number; enabled: number;
  staged: number; blocked: number; failed: number; skipped: number;
}
export interface SetupAgencyInput {
  name: string; type?: string; city?: string; state?: string;
}

export const officesApi = {
  list: () => api.get<{ items: OfficeItem[] }>('/company/offices'),
  create: (body: OfficeUpsertBody) => api.post<OfficeItem>('/company/offices', body),
  update: (id: string, body: Partial<OfficeUpsertBody>) =>
    api.patch<OfficeItem>(`/company/offices/${id}`, body),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/company/offices/${id}`),
  discoverBoards: (id: string, radiusMi: number) =>
    api.post<{ office: { id: string; slug: string; name: string }; radiusMi: number; note: string; count: number; agencies: NearbyAgency[] }>(
      `/company/offices/${id}/discover-boards?radius=${radiusMi}`,
    ),
  setupBoards: (id: string, agencies: SetupAgencyInput[]) =>
    api.post<{ office: { id: string; slug: string; name: string }; summary: SetupSummary; results: SetupOutcome[] }>(
      `/company/offices/${id}/setup-boards`, { agencies },
    ),
  // Re-score existing opportunities against the live office set (distances +
  // rank, no LLM). Reuses the proven sources backfill.
  rescoreOpportunities: () =>
    api.post<{ total: number; reranked: number; geocoded: number }>('/sources/backfill-enrichment?llm=0'),
};

export { API_BASE };
